import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CALLBACK_PATH, LbeAuthProvider, LoginCallback, RequireAuth, useLbeAuth } from '@laborelec/lbe-react/auth';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { TECHNODB_SCOPE } from './auth/AuthContext';
import LoginPage from './auth/LoginPage';
import oktaAuth from './auth/oktaConfig';
import Layout from './layouts/Layout';
import Comparator from './pages/Comparator';
import Database from './pages/Database';
import Faq from './pages/Faq';
import Home from './pages/Home';
import { api } from './services/Server';
import './styles/App.css';

if (['int', 'prod'].includes(import.meta.env.VITE_ENVIRONMENT)) {
  Sentry.init({
    dsn: 'https://b67f275ba9f9a07e0f88d9b76e4aed28@o4504841630384128.ingest.us.sentry.io/4510873431834624',
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    environment: import.meta.env.VITE_ENVIRONMENT,
    tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === 'prod' ? 0.25 : 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/technodb/],
    replaysOnErrorSampleRate: 1.0,
  });
}

if (import.meta.env.VITE_ENVIRONMENT === 'prod') {
  console.log = () => {};
  console.debug = () => {};
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnMount: false, refetchOnWindowFocus: false, retry: 2 },
  },
});

const toastOptions = {
  duration: 5000,
  style: {
    padding: '16px 20px',
    fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    borderRadius: '10px',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  success: {
    style: { background: '#2E7D32', color: 'white' },
    iconTheme: { primary: 'white', secondary: '#2E7D32' },
  },
  error: {
    style: { background: '#E30613', color: 'white' },
    iconTheme: { primary: 'white', secondary: '#E30613' },
  },
};

export default function App() {
  return (
    <>
      <Toaster position="bottom-right" toastOptions={toastOptions} />
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={Intl.DateTimeFormat().resolvedOptions().locale.toLocaleLowerCase()}
        >
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </LocalizationProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
}

/**
 * Inner component inside the router: after the sign-in, LbeAuthProvider navigates back to the page the user
 * asked for. Permissions come from the API's /me, reloaded whenever Okta renews the tokens.
 */
function AppRoutes() {
  const queryClient = useQueryClient();

  return (
    // Drop cached server data on sign-out so the next user doesn't briefly see the previous session's content.
    <LbeAuthProvider
      oktaAuth={oktaAuth}
      fetchMe={api.getMeRoles}
      scope={TECHNODB_SCOPE}
      onLogout={() => queryClient.clear()}
    >
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated to="/">
              <LoginPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path={CALLBACK_PATH}
          element={
            <LoginCallback
              appLabel={import.meta.env.VITE_OKTA_APP_LABEL}
              onError={(error) => Sentry.captureException(error)}
            />
          }
        />

        <Route element={<RequireAuth />}>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/database/entities/:entityKey" element={<Database />} />
            <Route path="/comparator" element={<Comparator />} />
            <Route path="/faq" element={<Faq />} />
          </Route>
        </Route>

        <Route path="/entities/:entityKey" element={<Navigate to="/database/entities/suppliers" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </LbeAuthProvider>
  );
}

/** Redirects authenticated users away from public-only routes (e.g. /login). */
function RedirectIfAuthenticated({
  children,
  to,
}: {
  children: React.ReactNode;
  to: string;
}) {
  const { isAuthenticated } = useLbeAuth();
  if (isAuthenticated) {
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}
